import { useEffect, useMemo, useState } from "react";
import { listToolRequests, listCategories, listTools, createToolRequest, reviewToolRequest } from "@/lib/firestore";
import { REQ } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, X, ClipboardText, CheckCircle, XCircle } from "@phosphor-icons/react";

const URGENCIES = ["low", "normal", "high"];

export default function ToolRequests() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canRequest = user?.permissions?.can_request_tools !== false;
  const [requests, setRequests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tools, setTools] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ tool_id: "", tool_name: "", category_id: "", quantity: 1, justification: "", urgency: "normal" });
  const [toolQuery, setToolQuery] = useState("");
  const [showToolDropdown, setShowToolDropdown] = useState(false);

  const [approveOn, setApproveOn] = useState(null); // request object
  const [approveForm, setApproveForm] = useState({ due_date: "", notes: "" });

  const load = async () => {
    const [r, c, t] = await Promise.all([listToolRequests(user), listCategories(), listTools({})]);
    setRequests(r);
    setCategories(c);
    setTools(t);
  };
  useEffect(() => { load(); }, []);

  const filteredTools = useMemo(() => {
    const q = toolQuery.trim().toLowerCase();
    if (!q) return tools;
    return tools.filter((t) => t.name.toLowerCase().includes(q) || (t.tool_code || "").toLowerCase().includes(q));
  }, [tools, toolQuery]);

  const openNewRequest = () => {
    setForm({ tool_id: "", tool_name: "", category_id: "", quantity: 1, justification: "", urgency: "normal" });
    setToolQuery("");
    setShowToolDropdown(false);
    setShowForm(true);
  };

  const selectExistingTool = (t) => {
    setForm((f) => ({ ...f, tool_id: t.id, tool_name: t.name, category_id: t.category_id || f.category_id }));
    setToolQuery(t.name);
    setShowToolDropdown(false);
  };

  const pending = useMemo(() => requests.filter((r) => r.status === "pending"), [requests]);
  const history = useMemo(() => requests.filter((r) => r.status !== "pending"), [requests]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.tool_id) {
      toast.error("Pick a tool from the list");
      return;
    }
    try {
      await createToolRequest({ ...form, quantity: parseInt(form.quantity) }, user);
      toast.success("Request submitted");
      setShowForm(false);
      setForm({ tool_id: "", tool_name: "", category_id: "", quantity: 1, justification: "", urgency: "normal" });
      setToolQuery("");
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const openApprove = (r) => {
    setApproveForm({ due_date: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10), notes: "" });
    setApproveOn(r);
  };

  const submitApprove = async (e) => {
    e.preventDefault();
    try {
      await reviewToolRequest(approveOn.id, "approve", user, approveForm);
      toast.success(`Tool issued to ${approveOn.requested_by_name}`);
      setApproveOn(null);
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const reject = async (id) => {
    try {
      await reviewToolRequest(id, "reject", user);
      toast.success("Request rejected");
      await load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs text-[#FF4F00] tracking-widest mb-2">// CHECKOUT</div>
          <h1 className="font-display font-black text-4xl">TOOL REQUESTS</h1>
          <div className="text-[#A1A1AA] mt-1 text-sm">
            {isAdmin ? "Approve checkout requests — approving issues the tool immediately." : "Request to check out a tool from inventory."}
          </div>
        </div>
        {canRequest && (
          <button
            data-testid={REQ.createBtn}
            onClick={openNewRequest}
            className="brutal-btn bg-[#FF4F00] text-black px-4 py-2.5 hover:bg-[#FF6B2B] flex items-center gap-2 transition-colors duration-150"
          >
            <Plus size={16} weight="bold" /> New Request
          </button>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div onClick={(e) => e.stopPropagation()} className="brutal-card p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <div>
                <div className="font-mono text-xs text-[#FF4F00] tracking-widest">// NEW TOOL REQUEST</div>
                <h2 className="font-display font-black text-2xl">Request Tool</h2>
              </div>
              <button onClick={() => setShowForm(false)} className="text-[#A1A1AA] hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <div className="relative">
                <input
                  data-testid={REQ.nameInput}
                  required
                  placeholder="Search tools..."
                  value={toolQuery}
                  onChange={(e) => { setToolQuery(e.target.value); setShowToolDropdown(true); if (form.tool_id) setForm((f) => ({ ...f, tool_id: "", tool_name: "" })); }}
                  onFocus={() => setShowToolDropdown(true)}
                  onBlur={() => setShowToolDropdown(false)}
                  className="w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]"
                />
                {showToolDropdown && (
                  <div className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto bg-[#121214] border border-[#27272A]">
                    {filteredTools.length === 0 && (
                      <div className="px-3 py-2 text-sm text-[#52525B] font-mono">// no matches //</div>
                    )}
                    {filteredTools.map((t) => (
                      <button
                        type="button"
                        key={t.id}
                        onMouseDown={() => selectExistingTool(t)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-[#1a1a1e] flex items-center justify-between gap-2"
                      >
                        <span className="truncate">{t.name}</span>
                        <span className="font-mono text-[10px] text-[#52525B] shrink-0">{t.available_quantity} avail</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <select data-testid={REQ.categorySelect} value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]">
                <option value="">Category (optional)</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <input data-testid={REQ.qtyInput} type="number" min="1" required placeholder="Quantity" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 font-mono focus:outline-none focus:border-[#FF4F00]" />
              <select data-testid={REQ.urgencySelect} value={form.urgency} onChange={(e) => setForm({ ...form, urgency: e.target.value })} className="w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]">
                {URGENCIES.map((u) => <option key={u} value={u}>{u} urgency</option>)}
              </select>
              <textarea data-testid={REQ.justificationInput} rows={4} required placeholder="Why do you need this tool?" value={form.justification} onChange={(e) => setForm({ ...form, justification: e.target.value })} className="w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]" />
              <button type="submit" data-testid={REQ.submitBtn} className="brutal-btn w-full bg-[#FF4F00] text-black px-4 py-3 hover:bg-[#FF6B2B] transition-colors duration-150">
                Submit Request →
              </button>
            </form>
          </div>
        </div>
      )}

      {approveOn && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setApproveOn(null)}>
          <div onClick={(e) => e.stopPropagation()} className="brutal-card p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <div>
                <div className="font-mono text-xs text-[#FF4F00] tracking-widest">// APPROVE & ISSUE</div>
                <h2 className="font-display font-black text-2xl">{approveOn.tool_name}</h2>
                <div className="font-mono text-xs text-[#A1A1AA]">to {approveOn.requested_by_name} · qty {approveOn.quantity}</div>
              </div>
              <button onClick={() => setApproveOn(null)} className="text-[#A1A1AA] hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={submitApprove} className="space-y-3">
              <input data-testid={REQ.approveDueDateInput} type="date" required value={approveForm.due_date} onChange={(e) => setApproveForm({ ...approveForm, due_date: e.target.value })} className="w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 font-mono focus:outline-none focus:border-[#FF4F00]" />
              <textarea data-testid={REQ.approveNotesInput} rows={2} placeholder="Notes (optional)" value={approveForm.notes} onChange={(e) => setApproveForm({ ...approveForm, notes: e.target.value })} className="w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]" />
              <button type="submit" data-testid={REQ.approveSubmitBtn} className="brutal-btn w-full bg-[#00E676] text-black px-4 py-3 hover:opacity-90 transition-opacity duration-150">
                Confirm & Issue →
              </button>
            </form>
          </div>
        </div>
      )}

      {renderSection("PENDING", pending, "// no pending requests //", "border-[#FFD700] text-[#FFD700]")}
      {renderSection("HISTORY", history, "// no history yet //", "border-[#27272A] text-[#52525B]")}
    </div>
  );

  function renderSection(title, items, emptyText, headerBadgeClass) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <h2 className="font-display font-black text-xl">{title}</h2>
          <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${headerBadgeClass}`}>{items.length}</span>
          <div className="h-px flex-1 bg-[#27272A]" />
        </div>
        {items.length === 0 && (
          <div className="brutal-card p-10 text-center text-[#52525B] font-mono">{emptyText}</div>
        )}
        {items.map((r) => (
          <div key={r.id} className="brutal-card p-5">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div className="flex items-start gap-2">
                <ClipboardText size={16} weight="bold" className="text-[#FF4F00] mt-1" />
                <div>
                  <div className="font-display font-bold text-lg">{r.tool_name}</div>
                  <div className="font-mono text-xs text-[#52525B]">by {r.requested_by_name} · qty {r.quantity} · {new Date(r.created_at).toLocaleDateString()}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${
                  r.urgency === "high" ? "border-[#D32F2F] text-[#D32F2F]" :
                  r.urgency === "normal" ? "border-[#FFD700] text-[#FFD700]" :
                  "border-[#A1A1AA] text-[#A1A1AA]"
                }`}>{r.urgency}</span>
                <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${
                  r.status === "pending" ? "border-[#FFD700] text-[#FFD700]" :
                  r.status === "approved" ? "border-[#00E676] text-[#00E676]" :
                  "border-[#D32F2F] text-[#D32F2F]"
                }`}>{r.status}</span>
              </div>
            </div>
            <div className="text-sm text-[#A1A1AA] mb-3">{r.justification}</div>
            {isAdmin && r.status === "pending" && (
              <div className="flex gap-2 border-t border-[#1a1a1e] pt-3">
                <button
                  data-testid={REQ.approveBtn(r.id)}
                  onClick={() => openApprove(r)}
                  className="brutal-btn border border-[#00E676] text-[#00E676] hover:bg-[#00E676] hover:text-black px-4 py-2 flex items-center gap-1 text-xs transition-colors duration-150"
                >
                  <CheckCircle size={14} weight="bold" /> Approve
                </button>
                <button
                  data-testid={REQ.rejectBtn(r.id)}
                  onClick={() => reject(r.id)}
                  className="brutal-btn border border-[#D32F2F] text-[#D32F2F] hover:bg-[#D32F2F] hover:text-white px-4 py-2 flex items-center gap-1 text-xs transition-colors duration-150"
                >
                  <XCircle size={14} weight="bold" /> Reject
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }
}
